import { useState, useEffect, type FC, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminPostsPath } from '../../shared/config/env';
import './Login.css';

/**
 * ログイン画面コンポーネント
 */
const Login: FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, requiresNewPassword } = useAuth();
  const navigate = useNavigate();
  const adminPostsPath = getAdminPostsPath();

  // 既に認証済みの場合は管理画面にリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigate(adminPostsPath, { replace: true });
    }
  }, [isAuthenticated, navigate, adminPostsPath]);

  // パスワード変更が必要な場合は新しいパスワード設定画面にリダイレクト
  useEffect(() => {
    if (requiresNewPassword) {
      navigate('/admin/new-password', { replace: true });
    }
  }, [requiresNewPassword, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(username, password);
      // パスワード変更が必要な場合は、newPasswordRequiredコールバックで
      // requiresNewPasswordがtrueになるため、useEffectでリダイレクトされる
      // 通常のログイン成功時のみここに到達
      // セッションが完全に保存されるまで少し待つ
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate(adminPostsPath, { replace: true });
    } catch (err: any) {
      if (err.code === 'NotAuthorizedException') {
        setError('ユーザー名またはパスワードが正しくありません');
      } else if (err.code === 'UserNotConfirmedException') {
        setError('アカウントが確認されていません');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('ログインに失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">管理画面ログイン</h1>
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}
          <div className="login-field">
            <label htmlFor="username">ユーザー名</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>
          <div className="login-field">
            <label htmlFor="password">パスワード</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
